import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, ApiError, getOrganizationContext, setOrganizationContext } from '../api/client'
import type { LoginResponse, Me } from '../api/types'

interface AuthState {
  me: Me | null
  loading: boolean
  /** true, solange kein Konto geladen ist und auch keines geladen werden konnte. */
  signedOut: boolean
  login: (email: string, password: string, rememberMe: boolean) => Promise<LoginResponse>
  logout: () => Promise<void>
  reload: () => Promise<void>
  can: (permission: string) => boolean
  enterOrganization: (organizationID: string | null) => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [signedOut, setSignedOut] = useState(false)

  const reload = useCallback(async () => {
    try {
      const next = await api.get<Me>('/me')
      setMe(next)
      setSignedOut(false)
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setMe(null)
        setSignedOut(true)
      } else if (error instanceof ApiError && error.needsPasswordChange) {
        // /me ist von der Schranke ausgenommen; dieser Fall sollte nicht auftreten.
        setSignedOut(false)
      } else {
        setMe(null)
        setSignedOut(true)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const login = useCallback(async (email: string, password: string, rememberMe: boolean) => {
    const response = await api.post<LoginResponse>('/auth/login', { email, password, rememberMe })
    // Der Organisationskontext eines vorherigen Benutzers darf nicht überdauern.
    setOrganizationContext(null)
    await reload()
    return response ?? { mustChangePassword: false }
  }, [reload])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      setOrganizationContext(null)
      setMe(null)
      setSignedOut(true)
    }
  }, [])

  const enterOrganization = useCallback(async (organizationID: string | null) => {
    setOrganizationContext(organizationID)
    await reload()
  }, [reload])

  const can = useCallback(
    (permission: string) => me?.permissions.includes(permission) ?? false,
    [me])

  const value = useMemo<AuthState>(
    () => ({ me, loading, signedOut, login, logout, reload, can, enterOrganization }),
    [me, loading, signedOut, login, logout, reload, can, enterOrganization])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth außerhalb von AuthProvider verwendet')
  return context
}

/** Der aktuell gewählte Organisationskontext, auch ohne geladenes Konto. */
export function useOrganizationContext(): string | null {
  return getOrganizationContext()
}
